import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatDate } from "../lib/api";
import { ResponsiveContainer, LineChart, Line, Tooltip, XAxis } from "recharts";
import { ShoppingCart, Warehouse, FileText, Plus, Trash2, Download, TrendingDown, TrendingUp, Eye, Pencil, X, Mail } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { ConfirmDialog } from "../components/ConfirmDialog";

const euro = (n) => `€ ${Number(n || 0).toFixed(2)}`;
const STATUS = { inviato: "text-stone-600 bg-stone-100", confermato: "text-amber-700 bg-amber-100", ricevuto: "text-brand bg-brand/10" };

export default function Procurement() {
  const [tab, setTab] = useState("cambusa");
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [cambusa, setCambusa] = useState([]);
  const [prods, setProds] = useState([]);
  const [prodQuery, setProdQuery] = useState("");
  const [cart, setCart] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [editProdQuery, setEditProdQuery] = useState("");
  const [confirmDelOrder, setConfirmDelOrder] = useState(null);
  const [receivedWarn, setReceivedWarn] = useState(null);
  const [editProds, setEditProds] = useState([]);
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [receivedDate, setReceivedDate] = useState("");
  const [rfrom, setRfrom] = useState("");
  const filteredProds = useMemo(() => {
    const qy = prodQuery.trim().toLowerCase();
    return prods.filter((p) => !qy || (p.name || "").toLowerCase().includes(qy) || (p.code || "").toLowerCase().includes(qy));
  }, [prods, prodQuery]);
  const editFilteredProds = useMemo(() => {
    if (!editOrder) return [];
    const qy = editProdQuery.trim().toLowerCase();
    return editProds.filter((p) => !editOrder.items.some((it) => it.normalized_name === p.normalized_name)
      && (!qy || (p.name || "").toLowerCase().includes(qy) || (p.code || "").toLowerCase().includes(qy)));
  }, [editProds, editOrder, editProdQuery]);
  const [rto, setRto] = useState("");
  const [rstatus, setRstatus] = useState("");
  const [orders, setOrders] = useState([]);
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [summary, setSummary] = useState({ suppliers: [], grand_total: 0 });
  const [sfrom, setSfrom] = useState("");
  const [sto, setSto] = useState("");
  const [chartMode, setChartMode] = useState("pack");

  const supObj = suppliers.find((s) => s.supplier_name === supplier);

  const loadOrders = useCallback(() => api.get("/procurement/orders").then(({ data }) => setOrders(data)).catch(() => {}), []);
  const loadSummary = useCallback(() => api.get("/procurement/summary", { params: { dfrom: sfrom || undefined, dto: sto || undefined } }).then(({ data }) => setSummary(data)).catch(() => {}), [sfrom, sto]);

  useEffect(() => {
    api.get("/procurement/suppliers").then(({ data }) => {
      setSuppliers(data);
      if (data.length) setSupplier(data[0].supplier_name);
    }).catch(() => {});
    loadOrders();
  }, [loadOrders]);

  useEffect(() => { setEmail(supObj?.email || ""); }, [supplier]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!supplier) return;
    api.get("/procurement/cambusa", { params: { supplier } }).then(({ data }) => setCambusa(data)).catch(() => {});
    api.get("/procurement/products", { params: { supplier } }).then(({ data }) => setProds(data)).catch(() => {});
    setCart([]);
  }, [supplier]);

  useEffect(() => { if (tab === "riepilogo") loadSummary(); }, [tab, loadSummary]);

  const [pendingAdd, setPendingAdd] = useState(null);
  useEffect(() => {
    if (!pendingAdd) return;
    const p = prods.find((x) => x.normalized_name === pendingAdd);
    if (p) { addToCart(p); setPendingAdd(null); }
  }, [prods, pendingAdd]); // eslint-disable-line react-hooks/exhaustive-deps
  const switchToCheaper = (cheaper, nn) => {
    setProds([]);
    setSupplier(cheaper.supplier);
    setPendingAdd(nn);
    setTab("ordine");
    toast.success(`Passato a ${cheaper.supplier} — prodotto aggiunto all'ordine`);
  };
  const location = useLocation();
  useEffect(() => {
    const st = location.state;
    if (st?.supplier) { setProds([]); setSupplier(st.supplier); setTab("ordine"); if (st.addProduct) setPendingAdd(st.addProduct); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addToCart = (p) => {
    if (cart.find((c) => c.normalized_name === p.normalized_name)) return;
    setCart([...cart, { normalized_name: p.normalized_name, name: p.name, qty: 1, unit_price: p.last_price || 0 }]);
  };
  const updateQty = (nn, qty) => setCart(cart.map((c) => c.normalized_name === nn ? { ...c, qty: Math.max(0, Number(qty) || 0) } : c));
  const removeItem = (nn) => setCart(cart.filter((c) => c.normalized_name !== nn));
  const total = cart.reduce((s, c) => s + c.qty * c.unit_price, 0);

  const saveEmail = async () => {
    try { await api.put(`/suppliers/${encodeURIComponent(supplier)}/meta`, { email }); toast.success("Email fornitore salvata"); setSuppliers(suppliers.map((s) => s.supplier_name === supplier ? { ...s, email } : s)); }
    catch { toast.error("Errore salvataggio email"); }
  };

  const confirmOrder = async () => {
    if (!cart.length) { toast.error("Aggiungi almeno un prodotto"); return; }
    try {
      const { data } = await api.post("/procurement/orders", { supplier, items: cart, notes, delivery_date: deliveryDate });
      toast.success(data.emailed ? "Ordine inviato via email al fornitore" : "Ordine registrato (nessuna email fornitore configurata)");
      setCart([]); setNotes(""); setDeliveryDate(""); loadOrders(); setTab("storico");
    } catch (e) { toast.error("Errore creazione ordine"); }
  };

  const setStatus = async (oid, status) => {
    try { await api.patch(`/procurement/orders/${oid}/status`, { status }); loadOrders(); }
    catch { toast.error("Errore aggiornamento stato"); }
  };
  const downloadPdf = async (oid, withPrices = true) => {
    try { const { data } = await api.get(`/procurement/orders/${oid}/pdf`, { params: { prices: withPrices }, responseType: "blob" }); const u = URL.createObjectURL(data); const a = document.createElement("a"); a.href = u; a.download = `ordine_${oid.slice(0, 8)}${withPrices ? "" : "_senza_prezzi"}.pdf`; a.click(); URL.revokeObjectURL(u); }
    catch { toast.error("Errore download PDF"); }
  };
  const previewOrder = async () => {
    if (!cart.length) { toast.error("Aggiungi almeno un prodotto"); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post("/procurement/orders/preview", { supplier, items: cart, notes, delivery_date: deliveryDate }, { responseType: "blob" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(data));
    } catch { toast.error("Errore anteprima PDF"); }
    finally { setPreviewing(false); }
  };
  const closePreview = () => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); };
  const deleteOrder = (oid) => setConfirmDelOrder(oid);
  const doDeleteOrder = async () => {
    const oid = confirmDelOrder;
    try { await api.delete(`/procurement/orders/${oid}`); toast.success("Ordine eliminato"); loadOrders(); }
    catch { toast.error("Errore eliminazione ordine"); }
    setConfirmDelOrder(null);
  };
  const openEdit = (o) => {
    if (o.status === "ricevuto") { setReceivedWarn(o); return; }
    doOpenEdit(o);
  };
  const doOpenEdit = async (o) => {
    setEditOrder({ id: o.id, order_number: o.order_number, supplier: o.supplier, notes: o.notes || "", delivery_date: o.delivery_date || "", items: o.items.map((i) => ({ ...i })) });
    try { const { data } = await api.get("/procurement/products", { params: { supplier: o.supplier } }); setEditProds(data); }
    catch { setEditProds([]); }
  };
  const editSetItem = (idx, patch) => setEditOrder((e) => ({ ...e, items: e.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const editRemoveItem = (idx) => setEditOrder((e) => ({ ...e, items: e.items.filter((_, i) => i !== idx) }));
  const editAddProduct = (nn) => {
    const p = editProds.find((x) => x.normalized_name === nn);
    if (!p) return;
    setEditOrder((e) => e.items.some((it) => it.normalized_name === nn) ? e : ({ ...e, items: [...e.items, { normalized_name: p.normalized_name, name: p.name, qty: 1, unit_price: p.last_price || 0 }] }));
  };
  const editTotal = editOrder ? editOrder.items.reduce((s, c) => s + (Number(c.qty) || 0) * (Number(c.unit_price) || 0), 0) : 0;
  const editSupObj = editOrder ? suppliers.find((s) => s.supplier_name === editOrder.supplier) : null;
  const saveEdit = async () => {
    if (!editOrder.items.length) { toast.error("L'ordine deve contenere almeno un prodotto"); return; }
    try {
      await api.put(`/procurement/orders/${editOrder.id}`, { supplier: editOrder.supplier, notes: editOrder.notes, delivery_date: editOrder.delivery_date,
        items: editOrder.items.map((i) => ({ normalized_name: i.normalized_name, name: i.name, qty: Number(i.qty) || 0, unit_price: Number(i.unit_price) || 0 })) });
      toast.success("Ordine aggiornato"); setEditOrder(null); loadOrders();
    } catch { toast.error("Errore aggiornamento ordine"); }
  };
  const resendOrder = async (oid) => {
    try { const { data } = await api.post(`/procurement/orders/${oid}/resend`); toast.success(data.emailed ? "Ordine re-inviato via email al fornitore" : "Inviato (nessuna copia al fornitore)"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore invio email"); }
  };
  const confirmReceive = async () => {
    try { await api.patch(`/procurement/orders/${receiveTarget}/status`, { status: "ricevuto", received_date: receivedDate }); toast.success("Ordine segnato come ricevuto"); setReceiveTarget(null); loadOrders(); }
    catch { toast.error("Errore aggiornamento stato"); }
  };
  const downloadReport = async (fmt) => {
    try {
      const { data } = await api.get(`/procurement/orders/report.${fmt}`, { params: { dfrom: rfrom || undefined, dto: rto || undefined, status: rstatus || undefined }, responseType: "blob" });
      const u = URL.createObjectURL(data); const a = document.createElement("a"); a.href = u; a.download = `riepilogo_ordini.${fmt}`; a.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Errore download report"); }
  };

  const Tab = ({ id, icon: Icon, label }) => (
    <button data-testid={`proc-tab-${id}`} onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === id ? "bg-brand text-white" : "text-stone-600 hover:bg-stone-100"}`}>
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <div data-testid="procurement-page" className="space-y-6">
      <div>
        <h1 className="font-serif text-4xl font-light">Ordini fornitori</h1>
        <p className="text-stone-500 text-sm mt-1">Cambusa e creazione ordini di acquisto.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Tab id="cambusa" icon={Warehouse} label="Cambusa" />
        <Tab id="ordine" icon={ShoppingCart} label="Ordine" />
        <Tab id="storico" icon={FileText} label="Storico ordini" />
        <Tab id="riepilogo" icon={Warehouse} label="Riepilogo" />
        {tab !== "storico" && (
          <div className="ml-auto min-w-[220px]">
            <Select value={supplier} onValueChange={setSupplier}>
              <SelectTrigger data-testid="proc-supplier" className="w-full"><SelectValue placeholder="Seleziona fornitore" /></SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => <SelectItem key={s.supplier_name} value={s.supplier_name}>{s.supplier_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {tab === "cambusa" && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Andamento su</span>
            <div className="inline-flex rounded-full border border-stone-300 overflow-hidden">
              <button data-testid="chart-mode-pack" onClick={() => setChartMode("pack")}
                className={`px-3 py-1 text-xs font-semibold transition-colors ${chartMode === "pack" ? "bg-brand text-white" : "text-stone-600 hover:bg-stone-100"}`}>Prezzo confezione</button>
              <button data-testid="chart-mode-unit" onClick={() => setChartMode("unit")}
                className={`px-3 py-1 text-xs font-semibold transition-colors ${chartMode === "unit" ? "bg-brand text-white" : "text-stone-600 hover:bg-stone-100"}`}>Prezzo/pezzo</button>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-stone-200 text-xs uppercase tracking-[0.1em] text-stone-500">
              <th className="text-left py-3 px-4 font-bold">Prodotto</th><th className="text-left py-3 px-4 font-bold">Codice</th>
              <th className="text-left py-3 px-4 font-bold">UM</th><th className="text-left py-3 px-4 font-bold">Andamento 6 mesi</th>
              <th className="text-right py-3 px-4 font-bold">Ultimo prezzo</th><th className="text-right py-3 px-4 font-bold">Prezzo/pezzo</th></tr></thead>
            <tbody>
              {cambusa.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-stone-400">Nessun prodotto per questo fornitore</td></tr>}
              {cambusa.map((p) => {
                const chartData = chartMode === "unit" ? (p.real_series || p.series) : p.series;
                return (
                <tr key={p.normalized_name} data-testid={`cambusa-row-${p.normalized_name}`} className="border-b border-stone-100">
                  <td className="py-3 px-4 font-medium text-stone-800">{p.name}
                    {p.rise_alert && (
                      <span data-testid={`rise-${p.normalized_name}`} className="mt-1 flex items-center gap-1 text-xs font-semibold text-alert bg-red-50 px-2 py-0.5 rounded-full w-fit" title={`Ultimo prezzo ${euro(p.rise_alert.current_price)} sopra la media storica ${euro(p.rise_alert.avg_price)}`}>
                        <TrendingUp size={12} /> Rincaro +{p.rise_alert.change_pct}% vs media
                      </span>
                    )}
                    {p.cheaper_supplier && (
                      <button data-testid={`cheaper-${p.normalized_name}`} onClick={() => switchToCheaper(p.cheaper_supplier, p.normalized_name)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full hover:bg-emerald-200 transition-colors" title="Ordina da questo fornitore più economico">
                        <TrendingDown size={12} /> {p.cheaper_supplier.supplier} · {euro(p.cheaper_supplier.price)}
                      </button>
                    )}
                  </td>
                  <td className="py-3 px-4 text-stone-500">{p.code || "—"}</td>
                  <td className="py-3 px-4 text-stone-500">{p.unit || "—"}</td>
                  <td className="py-3 px-4 w-48">
                    {chartData.length > 1 ? (
                      <ResponsiveContainer width="100%" height={40}>
                        <LineChart data={chartData}><XAxis dataKey="month" hide /><Tooltip formatter={(v) => euro(v)} labelFormatter={(l) => l} />
                          <Line type="monotone" dataKey="price" stroke={chartMode === "unit" ? "#8B3A3A" : "#2C4A3B"} strokeWidth={2} dot={false} /></LineChart>
                      </ResponsiveContainer>
                    ) : <span className="text-xs text-stone-400">dati insufficienti</span>}
                  </td>
                  <td className="py-3 px-4 text-right font-semibold">{p.last_price != null ? euro(p.last_price) : "—"}<div className="text-xs text-stone-400 font-normal">{p.last_supplier}</div></td>
                  <td data-testid={`cambusa-realunit-${p.normalized_name}`} className="py-3 px-4 text-right">
                    {p.real_unit_price != null ? <span className="font-semibold text-brand">{euro(p.real_unit_price)}{p.unit_measure && p.unit_measure !== "pezzo" ? `/${p.unit_measure}` : ""}</span> : "—"}
                    {(p.unit_measure && p.unit_measure !== "pezzo") ? <div className="text-xs text-stone-400 font-normal">{p.pieces_per_pack} {p.unit_measure}/riga</div> : ((p.pieces_per_pack || 1) > 1 && <div className="text-xs text-stone-400 font-normal">{p.pieces_per_pack} pz/conf.</div>)}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "ordine" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-stone-200 rounded-sm p-5">
            <p className="text-xs uppercase tracking-[0.1em] font-bold text-stone-500 mb-3">Prodotti di {supplier}</p>
            <input data-testid="order-product-search" value={prodQuery} onChange={(e) => setProdQuery(e.target.value)} placeholder="Cerca prodotto per nome o codice…" className="w-full border border-stone-300 rounded-sm px-3 py-2 text-sm mb-2 outline-none focus:ring-2 focus:ring-brand/40" />
            <div className="space-y-1 max-h-[420px] overflow-auto">
              {filteredProds.map((p) => (
                <button key={p.normalized_name} data-testid={`add-prod-${p.normalized_name}`} onClick={() => addToCart(p)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm hover:bg-stone-50 text-left">
                  <span className="text-sm text-stone-700">{p.name} <span className="text-stone-400">{p.unit}</span>{p.code && <span data-testid={`order-code-${p.normalized_name}`} className="ml-1 text-[10px] text-stone-400">cod. {p.code}</span>}
                    {p.cheaper_supplier && <span data-testid={`order-cheaper-${p.normalized_name}`} role="button" onClick={(e) => { e.stopPropagation(); switchToCheaper(p.cheaper_supplier, p.normalized_name); }} className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-emerald-700 cursor-pointer hover:underline"><TrendingDown size={11} /> {p.cheaper_supplier.supplier} · {euro(p.cheaper_supplier.price)}</span>}
                  </span>
                  <span className="text-xs text-stone-500 flex items-center gap-2">
                    <span className="text-right">{euro(p.last_price)}{(p.unit_measure && p.unit_measure !== "pezzo") ? <span className="block text-[10px] text-brand">{euro(p.real_unit_price)}/{p.unit_measure}</span> : ((p.pieces_per_pack || 1) > 1 && <span className="block text-[10px] text-brand">{euro(p.real_unit_price)}/pz</span>)}</span>
                    <Plus size={14} className="text-brand" /></span>
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-sm p-5 flex flex-col">
            <p className="text-xs uppercase tracking-[0.1em] font-bold text-stone-500 mb-3">Ordine</p>
            <div className="flex-1 space-y-2">
              {cart.length === 0 && <p className="text-sm text-stone-400">Aggiungi prodotti dall'elenco a sinistra.</p>}
              {cart.map((c) => (
                <div key={c.normalized_name} data-testid={`cart-${c.normalized_name}`} className="flex items-center gap-2">
                  <span className="flex-1 text-sm text-stone-700">{c.name}</span>
                  <input data-testid={`qty-${c.normalized_name}`} type="number" min="0" value={c.qty} onChange={(e) => updateQty(c.normalized_name, e.target.value)}
                    className="w-16 border border-stone-300 rounded-sm px-2 py-1 text-sm text-right" />
                  <span className="w-20 text-right text-sm text-stone-500">{euro(c.qty * c.unit_price)}</span>
                  <button onClick={() => removeItem(c.normalized_name)} className="text-stone-400 hover:text-alert"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <div className="border-t border-stone-200 mt-3 pt-3 space-y-3">
              <div className="flex items-center gap-2">
                <input data-testid="supplier-email" type="email" placeholder="email fornitore" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 border border-stone-300 rounded-sm px-2 py-1 text-sm" />
                <button data-testid="save-supplier-email" onClick={saveEmail} className="text-xs font-semibold border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-100">Salva email</button>
              </div>
              <input data-testid="order-notes" placeholder="Note (opzionale)" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-stone-300 rounded-sm px-2 py-1 text-sm" />
              <label className="flex items-center gap-2 text-sm text-stone-500">Consegna richiesta
                <input data-testid="order-delivery" type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="border border-stone-300 rounded-sm px-2 py-1 text-sm" /></label>
              <div className="flex items-center justify-between">
                <span className="font-serif text-2xl">{euro(total)}</span>
                <div className="flex items-center gap-2">
                  <button data-testid="preview-order" onClick={previewOrder} disabled={previewing} className="rounded-full border border-stone-300 px-5 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50 flex items-center gap-1.5"><Eye size={15} /> {previewing ? "Generazione…" : "Anteprima PDF"}</button>
                  <button data-testid="confirm-order" onClick={confirmOrder} className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-hover transition-colors">Conferma e invia</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "riepilogo" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-stone-500 flex items-center gap-2">Dal <input data-testid="sum-from" type="date" value={sfrom} onChange={(e) => setSfrom(e.target.value)} className="border border-stone-300 rounded-sm px-2 py-1 text-sm" /></label>
            <label className="text-sm text-stone-500 flex items-center gap-2">Al <input data-testid="sum-to" type="date" value={sto} onChange={(e) => setSto(e.target.value)} className="border border-stone-300 rounded-sm px-2 py-1 text-sm" /></label>
            <span className="ml-auto text-sm text-stone-500">Totale periodo: <b className="text-brand">{euro(summary.grand_total)}</b></span>
          </div>
          <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-stone-200 text-xs uppercase tracking-[0.1em] text-stone-500">
                <th className="text-left py-3 px-4 font-bold">Fornitore</th><th className="text-right py-3 px-4 font-bold">Fatture</th>
                <th className="text-left py-3 px-4 font-bold">Ultimo acquisto</th><th className="text-right py-3 px-4 font-bold">Totale acquistato</th></tr></thead>
              <tbody>
                {summary.suppliers.length === 0 && <tr><td colSpan={4} className="py-12 text-center text-stone-400">Nessun acquisto nel periodo</td></tr>}
                {summary.suppliers.map((s) => (
                  <tr key={s.supplier} data-testid={`sum-row-${s.supplier}`} className="border-b border-stone-100">
                    <td className="py-3 px-4 font-medium text-stone-800">{s.supplier}</td>
                    <td className="py-3 px-4 text-right text-stone-500">{s.invoice_count}</td>
                    <td className="py-3 px-4 text-stone-500">{s.last_date ? new Date(s.last_date).toLocaleDateString("it-IT") : "—"}</td>
                    <td className="py-3 px-4 text-right font-semibold">{euro(s.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "storico" && (
        <div className="space-y-3">
          <div className="bg-white border border-stone-200 rounded-sm p-3 flex items-center gap-3 flex-wrap">
            <span className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Riepilogo periodo</span>
            <label className="text-sm text-stone-500 flex items-center gap-1.5">Dal <input data-testid="rep-from" type="date" value={rfrom} onChange={(e) => setRfrom(e.target.value)} className="border border-stone-300 rounded-sm px-2 py-1 text-sm" /></label>
            <label className="text-sm text-stone-500 flex items-center gap-1.5">Al <input data-testid="rep-to" type="date" value={rto} onChange={(e) => setRto(e.target.value)} className="border border-stone-300 rounded-sm px-2 py-1 text-sm" /></label>
            <select data-testid="rep-status" value={rstatus} onChange={(e) => setRstatus(e.target.value)} className="border border-stone-300 rounded-sm px-2 py-1 text-sm">
              <option value="">Tutti gli stati</option><option value="inviato">inviato</option><option value="confermato">confermato</option><option value="ricevuto">ricevuto</option>
            </select>
            <div className="ml-auto flex items-center gap-2">
              <button data-testid="orders-report-pdf" onClick={() => downloadReport("pdf")} className="text-xs font-semibold border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-100 flex items-center gap-1.5"><Download size={14} /> Riepilogo PDF</button>
              <button data-testid="orders-report-xlsx" onClick={() => downloadReport("xlsx")} className="text-xs font-semibold border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-100 flex items-center gap-1.5"><Download size={14} /> Riepilogo Excel</button>
            </div>
          </div>
          {orders.length === 0 && <p className="text-sm text-stone-400">Nessun ordine ancora.</p>}
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="bg-white border border-stone-200 rounded-sm p-4 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold text-stone-800">N. {o.order_number} · {o.supplier}</p>
                <p className="text-xs text-stone-400">{new Date(o.created_at).toLocaleDateString("it-IT")} · {o.items.length} prodotti · {euro(o.total)}{o.delivery_date ? ` · consegna ${formatDate(o.delivery_date)}` : ""}{o.updated_at ? ` · modificato il ${formatDate(o.updated_at)}` : ""}{o.received_date ? ` · ricevuto il ${formatDate(o.received_date)}` : ""}</p>
              </div>
              <select data-testid={`order-status-${o.id}`} value={o.status} onChange={(e) => { const v = e.target.value; if (v === "ricevuto") { setReceiveTarget(o.id); setReceivedDate(o.delivery_date || new Date().toISOString().slice(0, 10)); } else setStatus(o.id, v); }}
                className={`text-xs font-bold px-2 py-1 rounded-full border-0 ${STATUS[o.status] || ""}`}>
                <option value="inviato">inviato</option><option value="confermato">confermato</option><option value="ricevuto">ricevuto</option>
              </select>
              <button data-testid={`order-pdf-${o.id}`} onClick={() => downloadPdf(o.id, true)} className="text-xs font-semibold border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-100 flex items-center gap-1.5"><Download size={14} /> Scarica PDF</button>
              <button data-testid={`order-pdf-noprices-${o.id}`} onClick={() => downloadPdf(o.id, false)} className="text-xs font-semibold border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-100 flex items-center gap-1.5"><Download size={14} /> PDF senza prezzi</button>
              <button data-testid={`order-resend-${o.id}`} onClick={() => resendOrder(o.id)} className="text-xs font-semibold border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-100 flex items-center gap-1.5"><Mail size={14} /> Re-invia email</button>
              <button data-testid={`order-edit-${o.id}`} onClick={() => openEdit(o)} className="text-xs font-semibold border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-100 flex items-center gap-1.5"><Pencil size={14} /> Modifica</button>
              <button data-testid={`order-delete-${o.id}`} onClick={() => deleteOrder(o.id)} className="text-stone-400 hover:text-alert" title="Elimina ordine"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog open={!!confirmDelOrder} title="Elimina ordine" description="Vuoi eliminare definitivamente questo ordine? L'operazione non è reversibile." confirmLabel="Elimina" onConfirm={doDeleteOrder} onClose={() => setConfirmDelOrder(null)} />
      <ConfirmDialog open={!!receivedWarn} danger={false} title="Ordine già ricevuto" description="Questo ordine è in stato 'ricevuto'. Modificarlo può creare incoerenze contrattuali. Vuoi procedere comunque?" confirmLabel="Procedi" onConfirm={() => { const o = receivedWarn; setReceivedWarn(null); doOpenEdit(o); }} onClose={() => setReceivedWarn(null)} />
      {previewUrl && (
        <div data-testid="preview-modal" className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="bg-white rounded-sm w-full max-w-3xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200">
              <span className="font-serif text-xl text-stone-900">Anteprima ordine</span>
              <div className="flex items-center gap-2">
                <a data-testid="preview-download" href={previewUrl} download="anteprima_ordine.pdf" className="text-xs font-semibold border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-100">Scarica</a>
                <button data-testid="preview-close" onClick={closePreview} className="text-xs font-semibold border border-stone-300 rounded-full px-3 py-1.5 hover:bg-stone-100">Chiudi</button>
              </div>
            </div>
            <iframe data-testid="preview-frame" title="Anteprima ordine" src={previewUrl} className="flex-1 w-full rounded-b-sm" />
          </div>
        </div>
      )}
      {editOrder && (
        <div data-testid="edit-order-modal" className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditOrder(null)}>
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200">
              <div>
                <span className="font-serif text-lg text-stone-900">Modifica ordine N. {editOrder.order_number} · {editOrder.supplier}</span>
                {editSupObj && (editSupObj.email || editSupObj.phone) && <p data-testid="edit-supplier-contacts" className="text-xs text-stone-500 mt-0.5">{[editSupObj.email, editSupObj.phone].filter(Boolean).join(" · ")}</p>}
              </div>
              <button data-testid="edit-order-close" onClick={() => setEditOrder(null)} className="text-stone-400 hover:text-stone-700"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-auto p-5 space-y-2">
              {editOrder.items.length === 0 && <p className="text-sm text-stone-400">Nessun prodotto. Aggiungine uno o annulla.</p>}
              {editOrder.items.map((c, idx) => (
                <div key={c.normalized_name} data-testid={`edit-item-${c.normalized_name}`} className="flex items-center gap-2">
                  <span className="flex-1 text-sm text-stone-700">{c.name}</span>
                  <input data-testid={`edit-qty-${c.normalized_name}`} type="number" min="0" value={c.qty} onChange={(e) => editSetItem(idx, { qty: e.target.value })} className="w-16 border border-stone-300 rounded-sm px-2 py-1 text-sm text-right" />
                  <span className="text-stone-400 text-xs">×</span>
                  <input data-testid={`edit-price-${c.normalized_name}`} type="number" min="0" step="0.01" value={c.unit_price} onChange={(e) => editSetItem(idx, { unit_price: e.target.value })} className="w-24 border border-stone-300 rounded-sm px-2 py-1 text-sm text-right" />
                  <span className="w-24 text-right text-sm text-stone-500">{euro((Number(c.qty) || 0) * (Number(c.unit_price) || 0))}</span>
                  <button data-testid={`edit-remove-${c.normalized_name}`} onClick={() => editRemoveItem(idx)} className="text-stone-400 hover:text-alert"><Trash2 size={15} /></button>
                </div>
              ))}
              <div className="pt-2">
                <input data-testid="edit-product-search" value={editProdQuery} onChange={(e) => setEditProdQuery(e.target.value)} placeholder="Cerca prodotto per nome o codice…" className="w-full border border-stone-300 rounded-sm px-2 py-1 text-sm mb-2 outline-none focus:ring-2 focus:ring-brand/40" />
                <Select value="" onValueChange={editAddProduct}>
                  <SelectTrigger data-testid="edit-add-product" className="w-full"><SelectValue placeholder="+ Aggiungi prodotto" /></SelectTrigger>
                  <SelectContent>
                    {editFilteredProds.map((p) => <SelectItem key={p.normalized_name} value={p.normalized_name}>{p.name}{p.code ? ` (${p.code})` : ""} · {euro(p.last_price)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border-t border-stone-200 p-5 space-y-3">
              <input data-testid="edit-notes" placeholder="Note (opzionale)" value={editOrder.notes} onChange={(e) => setEditOrder({ ...editOrder, notes: e.target.value })} className="w-full border border-stone-300 rounded-sm px-2 py-1 text-sm" />
              <label className="flex items-center gap-2 text-sm text-stone-500">Consegna richiesta
                <input data-testid="edit-delivery" type="date" value={editOrder.delivery_date} onChange={(e) => setEditOrder({ ...editOrder, delivery_date: e.target.value })} className="border border-stone-300 rounded-sm px-2 py-1 text-sm" /></label>
              <div className="flex items-center justify-between">
                <span className="font-serif text-2xl">{euro(editTotal)}</span>
                <div className="flex items-center gap-2">
                  <button data-testid="edit-cancel" onClick={() => setEditOrder(null)} className="rounded-full border border-stone-300 px-5 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50">Annulla</button>
                  <button data-testid="edit-save" onClick={saveEdit} className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-hover transition-colors">Salva modifiche</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {receiveTarget && (
        <div data-testid="receive-modal" className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setReceiveTarget(null)}>
          <div className="bg-white rounded-sm w-full max-w-sm p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-stone-900">Conferma ricezione ordine</h3>
            <label className="block text-sm text-stone-500">Data effettiva di consegna
              <input data-testid="received-date" type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} className="mt-1 w-full border border-stone-300 rounded-sm px-2 py-1.5 text-sm" /></label>
            <div className="flex items-center justify-end gap-2">
              <button data-testid="receive-cancel" onClick={() => setReceiveTarget(null)} className="rounded-full border border-stone-300 px-5 py-2 text-sm font-semibold text-stone-600 hover:bg-stone-50">Annulla</button>
              <button data-testid="receive-confirm" onClick={confirmReceive} className="rounded-full bg-brand text-white px-6 py-2 text-sm font-semibold hover:bg-brand-hover transition-colors">Conferma</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
